// src/pages/curso/CursoPos.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Container,
  Divider, Fade, Paper, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

interface UnidadeCurricular {
  unidadeCurricularId: number;
  nomeDaUnidadeCurricular: string;
  ordem: number;
  cargaHoraria: number;
  cargaHorariaMaxima: number;
}

interface Turma {
  id: number | string;
  nomeTurma: string;
  nomeUnidadeOperativa?: string;
  formaDeExecucao?: string;
  planoDoCursoId?: number;
  informacoesParaVendaOnline?: string;
  informacoesDoPlanoDoCurso?: {
    nome?: string;
    objetivos?: string;
    perfilProfissional?: string;
    tipoDeAcao?: string;
  };
  horariosSemAbreviacao?: string[];
  dataPeriodoExecucaoInicialFormatada?: string;
  dataPeriodoExecucaoFinalFormatada?: string;
  recursosFinanceiros?: Array<{
    linkMatriculaOnlineDoRecursoFinanceiroNovo?: string;
  }>;
  ofertas?: Array<{
    formasDePagamento?: Array<{
      disponivelParaWeb?: boolean;
      meioDePagamentoDescricao?: string;
      numeroDeParcelas?: number;
      valorDaParcela?: number;
      totalPagamento?: number;
    }>;
  }>;
  linkInscricao?: string;
  linkInscricaoEAD?: string;
  investimento?: {
    boleto?: {
      numeroParcelas: number;
      valorParcela: number;
      valorTotal: number;
    };
    cartaoCredito?: {
      parcelamentoMaximo: number;
      modulos: Array<{
        titulo: string;
        descricao: string;
        valor: number;
      }>;
    };
  };
  /** extra para JSON manual */
  linkInteresse?: string;
}

// — Botão de dúvidas (mesmo da graduação)
const LINK_DUVIDAS = 'https://forms.gle/MgGEEyFWiscG5GV76';

// — Função que prioriza o link direto do recurso financeiro (PÓS)
const getLinkMatriculaDireta = (v: Turma | null | undefined) => {
  if (!v) return null;
  const direto = v.recursosFinanceiros?.find(r => r.linkMatriculaOnlineDoRecursoFinanceiroNovo)?.linkMatriculaOnlineDoRecursoFinanceiroNovo;
  // fallback: mantém seu comportamento atual se não houver o direto
  return (
    direto ||
    v.linkInscricao ||
    v.recursosFinanceiros?.[0]?.linkMatriculaOnlineDoRecursoFinanceiroNovo ||
    (v as any)?.linkMatriculaOnline ||
    v.linkInscricaoEAD ||
    v.linkInteresse ||
    null
  );
};

const currencyBRL = (n: number | string | undefined) =>
  Number(typeof n === 'string' ? Number(n) : (n ?? 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const modalidadeParaExibicao = (modalidade?: string, fallback = 'A definir') => {
  if (!modalidade) return fallback;

  const modalidadeNormalizada = modalidade.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  return modalidadeNormalizada === 'hibrido' ? 'Presencial' : modalidade;
};

const InvestimentoCurso = ({ investimento }: { investimento: NonNullable<Turma['investimento']> }) => {
  const { boleto, cartaoCredito } = investimento;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" fontWeight={700} color="#102a5e" sx={{ mb: 2 }}>
        Investimento
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: boleto && cartaoCredito ? 'minmax(240px, 0.8fr) 2fr' : '1fr' },
          gap: 2,
        }}
      >
        {boleto && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              borderColor: '#c9d8f2',
              background: 'linear-gradient(145deg, #ffffff 0%, #f4f8ff 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
              <ReceiptLongIcon sx={{ color: '#1554a3' }} />
              <Typography variant="h6" fontWeight={700} color="#102a5e">Boleto</Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} color="#e85d04">
              {boleto.numeroParcelas}x de {currencyBRL(boleto.valorParcela)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography color="text.secondary">Investimento total do curso</Typography>
            <Typography variant="h6" fontWeight={700} color="#102a5e">
              {currencyBRL(boleto.valorTotal)}
            </Typography>
          </Paper>
        )}

        {cartaoCredito && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              borderColor: '#c9d8f2',
              background: 'linear-gradient(145deg, #ffffff 0%, #f4f8ff 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
              <CreditCardIcon sx={{ color: '#1554a3' }} />
              <Typography variant="h6" fontWeight={700} color="#102a5e">Cartão de crédito</Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: `repeat(${cartaoCredito.modulos.length}, minmax(0, 1fr))` },
                gap: 2,
              }}
            >
              {cartaoCredito.modulos.map((modulo) => (
                <Box
                  key={modulo.titulo}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #d8e3f5',
                    textAlign: 'center',
                    bgcolor: '#fff',
                  }}
                >
                  <Typography fontWeight={700} color="#1554a3">{modulo.titulo}</Typography>
                  <Typography variant="body2" color="text.secondary">{modulo.descricao}</Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">Investimento do módulo</Typography>
                  <Typography variant="h5" fontWeight={800} color="#102a5e">
                    {currencyBRL(modulo.valor)}
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    Parcelado em até <Box component="span" fontWeight={800} color="#e85d04">{cartaoCredito.parcelamentoMaximo}x</Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const ConteudoCursoAccordion = ({ curso }: { curso: Turma }) => {
  const [painelAberto, setPainelAberto] = useState<string | false>('sobre');
  const secoes = [
    {
      id: 'sobre',
      titulo: 'Sobre o Curso',
      conteudo: curso.informacoesParaVendaOnline,
    },
    {
      id: 'objetivos',
      titulo: 'Objetivos',
      conteudo: curso.informacoesDoPlanoDoCurso?.objetivos,
    },
    {
      id: 'perfil',
      titulo: 'Perfil Profissional',
      conteudo: curso.informacoesDoPlanoDoCurso?.perfilProfissional,
    },
  ].filter((secao) => Boolean(secao.conteudo));

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {secoes.map((secao) => (
        <Accordion
          key={secao.id}
          expanded={painelAberto === secao.id}
          onChange={(_event, expandido) => setPainelAberto(expandido ? secao.id : false)}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: painelAberto === secao.id ? '#8fb2e3' : '#dbe4f1',
            borderRadius: '14px !important',
            overflow: 'hidden',
            transition: 'border-color 220ms ease, box-shadow 220ms ease',
            '&::before': { display: 'none' },
            '&.Mui-expanded': { m: 0, boxShadow: '0 10px 28px rgba(16, 42, 94, 0.09)' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#1554a3' }} />}
            aria-controls={`${secao.id}-conteudo`}
            id={`${secao.id}-cabecalho`}
            sx={{
              minHeight: { xs: 58, sm: 64 },
              px: { xs: 2, sm: 2.5 },
              bgcolor: painelAberto === secao.id ? '#f3f7fd' : '#fff',
              '&.Mui-expanded': { minHeight: { xs: 58, sm: 64 } },
              '& .MuiAccordionSummary-content': { my: 1.5 },
              '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
            }}
          >
            <Typography fontWeight={700} color="#102a5e" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              {secao.titulo}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`${secao.id}-conteudo`}
            sx={{
              px: { xs: 2, sm: 2.5 },
              pt: 1,
              pb: { xs: 2, sm: 2.5 },
              color: '#344054',
              lineHeight: 1.75,
              '& p:first-of-type': { mt: 0 },
              '& p:last-of-type': { mb: 0 },
            }}
          >
            <Box dangerouslySetInnerHTML={{ __html: secao.conteudo! }} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const MetodologiaAulas = ({ linkMatricula }: { linkMatricula?: string | null }) => {
  const horarios = [
    ['Quarta-feira', '18h às 22h'],
    ['Quinta-feira', '18h às 22h'],
    ['Sexta-feira', '18h às 22h'],
    ['Sábado (online ao vivo)', '8h às 12h'],
  ];

  return (
    <Box sx={{ mt: 3.5 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: { xs: 3.5, sm: 4 },
          border: '1px solid rgba(21, 84, 163, 0.1)',
          background: 'linear-gradient(145deg, #f9fbff 0%, #f1f6fc 100%)',
          boxShadow: '0 18px 45px rgba(16, 42, 94, 0.08)',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 190,
            height: 190,
            borderRadius: '50%',
            top: -125,
            right: -80,
            background: 'rgba(21, 84, 163, 0.055)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '14px',
                bgcolor: '#fff',
                boxShadow: '0 7px 18px rgba(21, 84, 163, 0.12)',
              }}
            >
              <CalendarMonthIcon sx={{ color: '#1554a3', fontSize: 22 }} />
            </Box>
            <Typography variant="h6" fontWeight={750} color="#102a5e">Metodologia das aulas</Typography>
          </Box>
          <Typography color="#475467" sx={{ lineHeight: 1.75, maxWidth: 900 }}>
            É ofertada uma disciplina por mês, cujas aulas acontecem em até dois finais de semana consecutivos, dependendo da carga horária prevista para cada disciplina na estrutura curricular do curso.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.25,
              mt: 2.75,
            }}
          >
            {horarios.map(([dia, horario]) => (
              <Box
                key={dia}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: { xs: 1.5, sm: 1.75 },
                  py: 1.35,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.88)',
                  border: '1px solid rgba(21, 84, 163, 0.1)',
                  boxShadow: '0 7px 20px rgba(16, 42, 94, 0.055)',
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: 34,
                    height: 34,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '11px',
                    color: '#1554a3',
                    bgcolor: '#edf4fc',
                  }}
                >
                  <AccessTimeRoundedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700} color="#102a5e" sx={{ lineHeight: 1.25 }}>{dia}</Typography>
                  <Typography variant="body2" color="#667085">{horario}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {linkMatricula && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            href={linkMatricula}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<ArrowForwardIcon />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 360 },
              minHeight: 52,
              px: { xs: 2.5, sm: 5 },
              borderRadius: '16px',
              background: 'linear-gradient(120deg, #124b91 0%, #40368f 52%, #762f80 100%)',
              backgroundSize: '180% 180%',
              backgroundPosition: '0% 50%',
              color: '#fff',
              fontWeight: 750,
              fontSize: { xs: '0.98rem', sm: '1.1rem' },
              letterSpacing: '0.01em',
              boxShadow: '0 10px 24px rgba(45, 55, 140, 0.2)',
              transition: 'transform 220ms ease, box-shadow 220ms ease, background-position 420ms ease',
              '& .MuiButton-endIcon': { transition: 'transform 220ms ease' },
              '&:hover': {
                background: 'linear-gradient(120deg, #124b91 0%, #40368f 52%, #762f80 100%)',
                backgroundSize: '180% 180%',
                backgroundPosition: '100% 50%',
                transform: 'translateY(-2px)',
                boxShadow: '0 14px 30px rgba(64, 54, 143, 0.28)',
                '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
              },
              '&:active': { transform: 'translateY(0)' },
              '&:focus-visible': { outline: '3px solid rgba(64, 54, 143, 0.28)', outlineOffset: 3 },
            }}
          >
            Faça sua Matrícula
          </Button>
        </Box>
      )}
    </Box>
  );
};

async function getWithFallback(paths: string[]) {
  let last: any = null;
  for (const u of paths) {
    try {
      return (await axios.get(u, { timeout: 20000 })).data;
    } catch (e) {
      last = e;
    }
  }
  throw last;
}

export default function CursoPos() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [curso, setCurso] = useState<Turma | null>(null);
  const [matriz, setMatriz] = useState<UnidadeCurricular[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMatriz, setLoadingMatriz] = useState(false);
  const [tab, setTab] = useState(0);
  const [isEmBreve, setIsEmBreve] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const turmasURLs = [
    '/turmas',
    '/api/turmas',
    `http://${host}:5000/turmas`,
    `http://${host}:5000/api/turmas`,
    'http://localhost:5000/turmas',
    'http://localhost:5000/api/turmas',
  ];
  const planoURLs = (pid: number | string) => [
    `/plano-curso/${pid}`,
    `/api/plano-curso/${pid}`,
    `http://${host}:5000/plano-curso/${pid}`,
    `http://${host}:5000/api/plano-curso/${pid}`,
    'http://localhost:5000/plano-curso/' + pid,
    'http://localhost:5000/api/plano-curso/' + pid,
  ];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getWithFallback(turmasURLs);
        const ativas: Turma[] = data?.turmasAtivas || [];
        const emBreve: Turma[] = data?.turmasEmBreve || [];

        let found = ativas.find(c => String(c.id) === String(id)) || null;
        let manual = false;

        if (!found) {
          found = emBreve.find(c => String(c.id) === String(id)) || null;
          manual = !!found; // veio do JSON manual (emBreve)
        }

        if (!found) return navigate('/404');

        // Para cursos manuais (emBreve), garantimos formaDeExecucao padrão 'EAD' caso não venha preenchida
        if (manual && !found.formaDeExecucao) {
          found.formaDeExecucao = 'EAD';
        }

        setCurso(found);
        setIsEmBreve(manual);

        // Carrega matriz apenas para cursos ativos com plano
        if (!manual && tab === 1 && found.planoDoCursoId && matriz.length === 0) {
          setLoadingMatriz(true);
          try {
            const d = await getWithFallback(planoURLs(found.planoDoCursoId));
            const arr: UnidadeCurricular[] =
              d?.data?.ordemDaUnidadeCurricular?.sort((a: any, b: any) => a.ordem - b.ordem) || [];
            setMatriz(arr);
          } finally {
            setLoadingMatriz(false);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    (async () => {
      if (!isEmBreve && tab === 1 && curso?.planoDoCursoId && matriz.length === 0) {
        setLoadingMatriz(true);
        try {
          const d = await getWithFallback(planoURLs(curso.planoDoCursoId));
          const arr: UnidadeCurricular[] =
            d?.data?.ordemDaUnidadeCurricular?.sort((a: any, b: any) => a.ordem - b.ordem) || [];
          setMatriz(arr);
        } finally {
          setLoadingMatriz(false);
        }
      }
    })();
    // eslint-disable-next-line
  }, [tab, curso, isEmBreve]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  if (!curso) return <Typography sx={{ textAlign: 'center', py: 6 }} color="error">Curso não encontrado</Typography>;

  const formas = curso?.ofertas?.[0]?.formasDePagamento || [];
  const duracao = curso?.dataPeriodoExecucaoInicialFormatada
    ? `De ${curso.dataPeriodoExecucaoInicialFormatada} até ${curso.dataPeriodoExecucaoFinalFormatada}`
    : 'A definir';

  // Fallbacks de link de matrícula/inscrição
  const linkMatriculaAtivo =
    curso?.linkInscricao ||
    curso?.recursosFinanceiros?.[0]?.linkMatriculaOnlineDoRecursoFinanceiroNovo ||
    (curso as any)?.linkMatriculaOnline ||
    null;

  const linkInscricaoEAD =
    curso?.linkInscricaoEAD ||
    linkMatriculaAtivo ||
    curso?.linkInteresse || // do JSON manual
    null;

  const isEAD = (curso.formaDeExecucao || '').toUpperCase().includes('EAD');
  const modalidadeExibicao = modalidadeParaExibicao(curso.formaDeExecucao, isEmBreve ? 'EAD' : 'A definir');

  const handleTab = (_e: any, v: number) => {
    setTabLoading(true);
    setTimeout(() => {
      setTab(v);
      setTabLoading(false);
    }, 180);
  };

  return (
    <Container sx={{ py: 5 }}>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 4, bgcolor: '#2b6cb0', '&:hover': { bgcolor: '#2c5282' } }}
      >
        Voltar para Cursos
      </Button>

      <Typography
        variant="h4"
        fontWeight={600}
        textAlign="center"
        gutterBottom
        sx={{ fontSize: { xs: '1.55rem', sm: '2.125rem' }, lineHeight: 1.25, mb: 1.5 }}
      >
        {curso.nomeTurma}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, sm: 4 } }}>
        <Box
          role="status"
          aria-label={`Modalidade do curso: ${modalidadeExibicao}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.75,
            py: 1,
            borderRadius: 999,
            bgcolor: '#fff8f1',
            border: '1px solid rgba(201, 107, 36, 0.2)',
            boxShadow: '0 6px 18px rgba(16, 42, 94, 0.06)',
          }}
        >
          <SchoolOutlinedIcon sx={{ color: '#c96b24', fontSize: 21 }} />
          <Typography variant="body2" color="#667085">Modalidade</Typography>
          <Box sx={{ width: 1, height: 18, bgcolor: 'rgba(201, 107, 36, 0.22)' }} />
          <Typography variant="body2" fontWeight={750} color="#102a5e">{modalidadeExibicao}</Typography>
        </Box>
      </Box>

      {isEmBreve ? (
        // ——————————————————————————————————————————————
        // BLOCO ESPECIAL: JSON MANUAL (emBreve) — mostrar infos do curso + botão EAD
        // ——————————————————————————————————————————————
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, mb: 5, borderRadius: { xs: 2.5, sm: 3 } }}>
          {curso.informacoesParaVendaOnline || curso.informacoesDoPlanoDoCurso?.objetivos || curso.informacoesDoPlanoDoCurso?.perfilProfissional ? (
            <ConteudoCursoAccordion curso={curso} />
          ) : (
            <Typography sx={{ mb: 2 }}>
              Este curso de Pós-Graduação é ofertado na modalidade <strong>EAD</strong>. A inscrição ocorre diretamente no portal EAD.
            </Typography>
          )}

          {curso.investimento && <InvestimentoCurso investimento={curso.investimento} />}

          {curso.investimento && <MetodologiaAulas linkMatricula={linkInscricaoEAD} />}

          {/* Botão de inscrição EAD / interesse */}
          {linkInscricaoEAD && !curso.investimento && (
            <Button
              variant="contained"
              sx={{ mt: 3, backgroundColor: '#2b6cb0', '&:hover': { backgroundColor: '#2c5282' } }}
              href={linkInscricaoEAD}
              target="_blank"
              rel="noopener noreferrer"
            >
              {curso.linkInteresse && !curso.linkInscricaoEAD && !linkMatriculaAtivo
                ? 'Registrar Interesse'
                : 'Inscreva-se no Portal EAD'}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {/* Cursos ATIVOS (API) — mantém as abas */}
          <Tabs value={tab} onChange={handleTab} variant="scrollable" scrollButtons="auto" sx={{ mb: 4 }}>
            <Tab label="Sobre o Curso" />
            <Tab label="Matriz Curricular" />
            <Tab label="Coordenação" />
            <Tab label="Investimento" />
          </Tabs>

          <Paper elevation={2} sx={{ p: { xs: 2, sm: 4 }, mb: 5, minHeight: 250, borderRadius: { xs: 2.5, sm: 3 } }}>
            {tabLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <Fade in timeout={300}>
                <Box>
                  {tab === 0 && (
                    <ConteudoCursoAccordion curso={curso} />
                  )}

                  {tab === 1 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Matriz Curricular</Typography>
                      {loadingMatriz ? (
                        <Box py={2}><CircularProgress /></Box>
                      ) : matriz.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Ordem</TableCell>
                              <TableCell>Unidade Curricular</TableCell>
                              <TableCell>Carga Horária</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matriz.map(uc => (
                              <TableRow key={uc.unidadeCurricularId}>
                                <TableCell>{uc.ordem}</TableCell>
                                <TableCell>{uc.nomeDaUnidadeCurricular}</TableCell>
                                <TableCell>{uc.cargaHoraria}h</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography>Matriz curricular não disponível</Typography>
                      )}
                    </Box>
                  )}

                  {tab === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Coordenação do Curso</Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography>
                        <strong>E-mail:</strong>{' '}
                        <a href="mailto:pos.graduacao@am.senac.br">pos.graduacao@am.senac.br</a>
                      </Typography>
                      <Typography>
                        <strong>Contato:</strong>{' '}
                        <a href="tel:+559231999956">(92) 3199-9956</a>
                      </Typography>
                    </Box>
                  )}

                  {tab === 3 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Investimento</Typography>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1">Opções de Pagamento no semestre:</Typography>
                        {formas.length > 0 ? (
                          formas.map((f, i) =>
                            f?.disponivelParaWeb ? (
                              <Box key={i} sx={{ mb: 2 }}>
                                <Typography fontWeight={600}>{f.meioDePagamentoDescricao}</Typography>
                                {Number(f.numeroDeParcelas || 0) > 1 ? (
                                  <>
                                    <Typography>Parcelas: {f.numeroDeParcelas}x de {currencyBRL(f.valorDaParcela)}</Typography>
                                    <Typography>Total: {currencyBRL(f.totalPagamento)}</Typography>
                                  </>
                                ) : (
                                  <Typography>À vista: {currencyBRL(f.totalPagamento)}</Typography>
                                )}
                                {i < formas.length - 1 && <Divider sx={{ my: 2 }} />}
                              </Box>
                            ) : null
                          )
                        ) : (
                          <Typography>Informações de investimento não disponíveis.</Typography>
                        )}
                      </Box>

                      <Typography><strong>Duração:</strong> {duracao}</Typography>
                      <Typography><strong>Curso:</strong> {modalidadeExibicao}</Typography>

                      {(linkMatriculaAtivo || linkInscricaoEAD) && (
                        <Button
                          variant="contained"
                          sx={{ mt: 3, backgroundColor: '#38a169', '&:hover': { backgroundColor: '#2f855a' } }}
                          href={isEAD ? linkInscricaoEAD || linkMatriculaAtivo! : linkMatriculaAtivo!}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {isEAD ? 'Inscreva-se (EAD)' : 'Fazer Matrícula'}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            )}
          </Paper>
          {/* ——————————————————————————————————————————————
    BLOCO FINAL: “Inscrições Pós-Graduação Senac AM”
    (prioriza linkMatriculaOnlineDoRecursoFinanceiroNovo)
    —————————————————————————————————————————————— */}
<Box sx={{ backgroundColor:'#f9fbff', p:4, borderRadius:4, textAlign:'center' }}>
  <Typography variant="h5" gutterBottom>Matrículas abertas para Pós-Graduação Senac AM</Typography>
  <Divider sx={{ mb:2 }} />
  <Typography>Matricule-se nos Cursos de Pós-graduação Senac AM!</Typography>
  <Typography sx={{ mt:2 }}>Escolha a unidade do seu curso e clique no botão abaixo.</Typography>

  <Box sx={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:2, mt:3 }}>
    {[curso].filter(Boolean).map((v) => {
      const href = getLinkMatriculaDireta(v) || '#';
      return (
        <Button
          key={String(v!.id)}
          variant="contained"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ borderRadius:'24px', backgroundColor:'#E3006D', '&:hover':{ backgroundColor:'#9F004D' } }}
        >
          {`Matrícula para a unidade ${v!.nomeUnidadeOperativa || '—'}`}
        </Button>
      );
    })}
  </Box>

  {/* 🔗 Link de tira-dúvidas na matrícula (mesmo da graduação) */}
  <Box sx={{ mt:3 }}>
    <Button
      variant="outlined"
      href={LINK_DUVIDAS}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        borderRadius: '24px',
        borderColor: '#E3006D',
        color: '#E3006D',
        fontWeight: 600,
        '&:hover': {
          borderColor: '#9F004D',
          backgroundColor: 'rgba(227,0,109,0.05)',
        },
      }}
    >
      DÚVIDAS NA MATRÍCULA? FALE CONOSCO
    </Button>
  </Box>
</Box>
        </>
      )}
    </Container>
  );
}
